// The one interactive screen of /claim: choose a permanent password.
//
// The rule checklist is live and the submit button stays disabled until every rule
// passes, but the CLIENT IS NOT THE AUTHORITY — a password Cognito rejects comes
// back as a 400 with the policy message, which is rendered verbatim below the form.
import { useEffect, useRef, useState } from 'react';
import { PrimaryButton, card } from '../components/ui';
import { PASSWORD_RULES, meetsPolicy } from '../lib/passwordPolicy';
import { ClaimShell } from './ClaimShell';
import { claimBadge, type ClaimKind } from './ClaimSuccess';

export function SetPasswordForm({
  firstName,
  expiresAt,
  kind,
  busy,
  serverError,
  onSubmit,
}: {
  firstName?: string;
  expiresAt?: string;
  kind: ClaimKind;
  busy: boolean;
  serverError: string | null;
  onSubmit: (password: string) => void;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reveal, setReveal] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Land focus on the first field — the whole page exists to fill it in.
  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  const policyOk = meetsPolicy(password);
  const matches = password.length > 0 && password === confirm;
  const mismatch = confirmTouched && confirm.length > 0 && !matches;
  const canSubmit = policyOk && matches && !busy;

  const heading = firstName ? `Welcome, ${firstName}. Set your password.` : 'Set your password.';

  return (
    <ClaimShell
      badge={claimBadge(kind)}
      footnote="This link works once. Your password is set directly with our identity provider."
    >
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: 'var(--teal-deep)',
            textTransform: 'uppercase',
            letterSpacing: '.14em',
          }}
        >
          Claim your account
        </div>
        <h1
          className="pretty"
          style={{ fontSize: 32, fontWeight: 800, lineHeight: 1.16, letterSpacing: '-.015em', margin: '12px 0 0' }}
        >
          {heading}
        </h1>
        <p className="pretty" style={{ fontSize: 16, color: 'var(--slate)', fontWeight: 500, lineHeight: 1.5, margin: '10px 0 0' }}>
          {kind === 'provider' && 'One password, then your provider account is ready to sign into.'}
          {kind === 'admin' && 'One password, then your account is ready to sign into.'}
          {kind === 'patient' && 'One password, then your plan is ready and waiting in the app.'}
        </p>
        {expiresAt && <ExpiryNote expiresAt={expiresAt} />}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit(password);
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <PasswordField
            id="claim-password"
            inputRef={passwordRef}
            label="New password"
            value={password}
            reveal={reveal}
            onChange={setPassword}
            describedBy="claim-password-rules"
          />
          <PasswordField
            id="claim-password-confirm"
            label="Confirm password"
            value={confirm}
            reveal={reveal}
            onChange={(v) => {
              setConfirm(v);
              setConfirmTouched(true);
            }}
            onBlur={() => setConfirmTouched(true)}
            invalid={mismatch}
            describedBy={mismatch ? 'claim-password-mismatch' : undefined}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={() => setReveal((r) => !r)}
            aria-pressed={reveal}
            style={{ fontSize: 13, fontWeight: 800, color: 'var(--teal-deep)', padding: '4px 0' }}
          >
            {reveal ? 'Hide passwords' : 'Show passwords'}
          </button>
        </div>

        <ul
          id="claim-password-rules"
          style={{ listStyle: 'none', margin: '16px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {PASSWORD_RULES.map((rule) => {
            const ok = rule.test(password);
            return (
              <li key={rule.id} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <RuleMark ok={ok} />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: ok ? 'var(--teal-deep)' : 'var(--slate)',
                  }}
                >
                  {rule.label}
                </span>
              </li>
            );
          })}
        </ul>

        <div id="claim-password-mismatch" aria-live="polite" style={{ minHeight: mismatch ? undefined : 0 }}>
          {mismatch && (
            <div style={{ fontSize: 13, fontWeight: 700, color: '#C2453F', marginTop: 12 }}>
              The two passwords don’t match yet.
            </div>
          )}
        </div>

        <div style={{ marginTop: 24 }}>
          <PrimaryButton full type="submit" disabled={!canSubmit}>
            {busy ? 'Setting your password…' : 'Set my password'}
          </PrimaryButton>
        </div>

        {/* Server is the authority on the policy — render its message as-is. */}
        <div aria-live="assertive" role="status">
          {serverError && (
            <div
              style={{
                ...card,
                marginTop: 16,
                padding: '13px 15px',
                fontSize: 13,
                fontWeight: 700,
                color: '#C2453F',
                boxShadow: 'none',
                background: '#FDF2F1',
                borderRadius: 10,
              }}
            >
              {serverError}
            </div>
          )}
        </div>
      </form>
    </ClaimShell>
  );
}

function ExpiryNote({ expiresAt }: { expiresAt: string }) {
  const when = new Date(expiresAt);
  if (Number.isNaN(when.getTime())) return null;
  const hours = Math.round((when.getTime() - Date.now()) / 3_600_000);
  if (hours <= 0) return null;
  const label =
    hours < 24
      ? `in about ${hours} ${hours === 1 ? 'hour' : 'hours'}`
      : `on ${when.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}`;
  return (
    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', margin: '12px 0 0' }}>
      This link expires {label}.
    </p>
  );
}

function RuleMark({ ok }: { ok: boolean }) {
  if (!ok) {
    return <span style={{ width: 16, height: 16, borderRadius: '50%', background: 'var(--option-empty)', flexShrink: 0 }} aria-hidden />;
  }
  return (
    <svg width={16} height={16} viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }} aria-hidden>
      <circle cx={10} cy={10} r={10} fill="var(--teal)" />
      <path d="M5.8 10.2l2.8 2.8 5.6-5.8" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PasswordField({
  id,
  label,
  value,
  reveal,
  onChange,
  onBlur,
  invalid,
  describedBy,
  inputRef,
}: {
  id: string;
  label: string;
  value: string;
  reveal: boolean;
  onChange: (v: string) => void;
  onBlur?: () => void;
  invalid?: boolean;
  describedBy?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--slate)', marginBottom: 6 }}>
        {label}
      </label>
      <div style={{ ...card, padding: '4px 8px', boxShadow: invalid ? '0 0 0 2px #E6A9A4' : 'var(--card-shadow)' }}>
        <input
          id={id}
          ref={inputRef}
          type={reveal ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          autoComplete="new-password"
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          style={{
            border: 'none',
            outline: 'none',
            width: '100%',
            padding: '12px',
            fontSize: 16,
            fontWeight: 600,
            background: 'transparent',
          }}
        />
      </div>
    </div>
  );
}
